/* RESPONSIBLE TEAM: team-workflows */
import type Model from '@ember-data/model';
import { statisticKeys } from 'embercom/models/data/outbound/constants';
import { inject as service } from '@ember/service';
import { type TaskGenerator } from 'ember-concurrency';
import { setOwner } from '@ember/application';
import type { BotConfigTarget } from '../configuration';
import {
  AI_AGENT_WORKFLOW_TARGET,
  CONVERSATION_STARTED_TARGET,
} from 'embercom/lib/operator/custom-bots/constants';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import { underscore } from '@ember/string';

const SORT_BY_PRIORITY = 'priority';
const SORT_DIRECTION_ASC = 'asc';
const MAX_OPEN_COUNT_FOR_AUTOMATIC_OPEN = 5;

export const PAGE_SIZE = 25;

export default class CustomBotListData {
  @service appService: any;
  @service outboundHomeService: any;
  @service store: any;
  appId;
  target;
  objectType;
  statisticKeys: number[];
  currentPage = 0;
  // we set a created_from_standalone_app flag on messenger workflows to indicate if it was created from a standalone app
  // the intention is to show workflows created from standalone apps on the workflows overview page and vice versa
  // for other custom bot objects like tasks we don't need to set this flag as created_from_standalone_app is not relevant and always false
  // this is only important for the migration to suite flow
  botCreatedFromStandalone: boolean;

  @tracked bots: any[] = [];
  @tracked totalCount = 0;
  @tracked isOpen = window.sessionStorage.getItem(`${this._uniqueIdentifier}-isOpen`) === 'true';
  @tracked title: string | null = null;

  constructor(
    owner: string,
    appId: string,
    target: BotConfigTarget,
    objectType: number,
    botCreatedFromStandalone = false,
    statisticKeys: number[] = [], // Specify statistic keys here to load stats upfront along with bots
  ) {
    setOwner(this, owner);
    this.appId = appId;
    this.target = target;
    this.objectType = objectType;
    this.statisticKeys = statisticKeys;
    this.botCreatedFromStandalone = botCreatedFromStandalone;
  }

  async contentSearch(pageFrom: number, pageSize?: number): Promise<object> {
    // If target channel filter is applied we only list bots that support Omnichannel
    if (
      this.outboundHomeService.additionalSearchableData?.targetChannels &&
      this.outboundHomeService.additionalSearchableData.targetChannels.length > 0 &&
      this.target !== CONVERSATION_STARTED_TARGET
    ) {
      return {
        totalCount: 0,
        contentWrappers: [],
      };
    }

    let response = await this.outboundHomeService.contentSearchWithFilters(
      this._getSearchParams(pageFrom, pageSize),
    );
    let serializedResponse = JSON.stringify({
      ...response,
      contentWrappers: response.contentWrappers.map((wrapper: Model) => wrapper.serialize()),
    });

    // Write the repsonse to the cache
    window.sessionStorage.setItem(this._responseKey(pageFrom), serializedResponse);

    return response;
  }

  @task({ restartable: true })
  *setupInitialState(pageSize?: number) {
    // Reconstruct the overview page state from the cache
    // Tries to load the data for all previously loaded pages
    let bots: any[] = [];
    let totalCount = 0;
    let currentPage = 0;
    let cachedResponse = JSON.parse(
      `${window.sessionStorage.getItem(this._responseKey(currentPage))}`,
    );
    while (cachedResponse) {
      bots = [...bots, ...cachedResponse.contentWrappers];
      totalCount = cachedResponse.totalCount;
      currentPage++;
      cachedResponse = JSON.parse(
        `${window.sessionStorage.getItem(this._responseKey(currentPage))}`,
      );
    }
    this.bots = this.outboundHomeService._contentWrapperModels(bots);
    this.totalCount = totalCount;
    this.currentPage = Math.max(0, currentPage - 1);
    // Only want to try and open the section if we have data
    if (this._hasCachedData) {
      this._maybeOpenSection();
    }

    // Perform a background reload that ensures we're looking at the most recent data
    yield taskFor(this.reload).perform(pageSize);
  }

  @task({ drop: true })
  *fetchNextPage(): TaskGenerator<void> {
    this.currentPage++;
    let response = yield this.contentSearch(this.currentPage);

    if (this.statisticKeys.length > 0) {
      yield this.loadStats(response.contentWrappers);
    }

    this.bots = [...this.bots, ...response.contentWrappers];
    this.totalCount = response.totalCount;
  }

  @task({ restartable: true })
  *reload(pageSize?: number): TaskGenerator<void> {
    let bots: any[] = [];
    let totalCount = 0;
    for (let page = 0; page <= this.currentPage; page++) {
      let response = yield this.contentSearch(page, pageSize);

      if (this.statisticKeys.length > 0) {
        yield this.loadStats(response.contentWrappers);
      }

      bots = [...bots, ...response.contentWrappers];
      totalCount = response.totalCount;
    }
    this.bots = bots;
    this.totalCount = totalCount;
    this._maybeOpenSection();
  }

  async loadStats(contentWrappers: any[]) {
    let newStats = await this.outboundHomeService.getStats({
      ruleset_ids: contentWrappers.map((contentWrapper) => contentWrapper.contentWrapperId),
      statistics: this.statisticKeys,
      app_id: this.appId,
    });
    newStats.forEach((statsForContentWrapper: any) => {
      statsForContentWrapper.forEach((stat: any) =>
        this.store.push(this.store.normalize('outbound/content-statistic', stat)),
      );
    });
  }

  setOpenState(isOpen: boolean) {
    this.isOpen = isOpen;
    window.sessionStorage.setItem(`${this._uniqueIdentifier}-isOpen`, `${isOpen}`);
  }

  _getSearchParams(pageFrom: number, pageSize: number = PAGE_SIZE) {
    let searchParams = {
      object_types: [this.objectType],
      app_id: this.appId,
      per_page: pageSize,
      page_from: pageFrom,
      statistics: [
        statisticKeys.receipts,
        statisticKeys.goals,
        statisticKeys.replies,
        statisticKeys.completions,
        statisticKeys.finStepReached,
      ],
      title: this.title,
      additionalSearchableData: {
        // Only send non-null values from additionalSearchableData
        ...this._prepareAdditionalSearchableData(),
        target: this.target,
      },
      sort_by: SORT_BY_PRIORITY,
      sort_direction: SORT_DIRECTION_ASC,
    };

    if (
      this.appService.app.hasStandaloneToSuiteMigrationFeature &&
      this.target !== AI_AGENT_WORKFLOW_TARGET
    ) {
      searchParams.additionalSearchableData.created_from_standalone_app =
        this.botCreatedFromStandalone;
    }

    return searchParams;
  }

  _hashParams(str: string) {
    let hash = 0;
    for (let character of str) {
      let charCode = character.charCodeAt(0);
      /* eslint-disable no-bitwise */
      hash = (hash << 5) - hash + charCode;
      /* eslint-disable no-bitwise */
      hash |= 0; // Convert to 32bit integer
    }
    return hash;
  }

  _maybeOpenSection() {
    if (
      // We don't want to override the user's choice if they've already opened/closed the panel
      !window.sessionStorage.getItem(`${this._uniqueIdentifier}-isOpen`) &&
      this.totalCount <= MAX_OPEN_COUNT_FOR_AUTOMATIC_OPEN
    ) {
      this.setOpenState(true);
    }
  }

  _responseKey(page: number) {
    return `${this._hashParams(JSON.stringify(this._getSearchParams(page)))}`;
  }

  _prepareAdditionalSearchableData() {
    let additionalSearchableData = {} as {
      target_channels?: string[];
      workflow_type?: string;
      parallel?: boolean;
      has_fin?: boolean;
      created_from_standalone_app?: boolean;
    };

    // Only send non-null values from additionalSearchableData
    if (this.outboundHomeService.additionalSearchableData) {
      Object.keys(this.outboundHomeService.additionalSearchableData).map((key) => {
        let snakeCasedKey = underscore(key) as keyof typeof additionalSearchableData;
        let value = this.outboundHomeService.additionalSearchableData[key];
        if (value !== null) {
          additionalSearchableData[snakeCasedKey] = value;
        }
      });
    }

    return additionalSearchableData;
  }

  get _hasCachedData() {
    return !!window.sessionStorage.getItem(this._responseKey(0));
  }

  get _uniqueIdentifier() {
    return `${this.appId}-${this.target}-${this.objectType}`;
  }

  get loading() {
    // We define loading here as when we are fetching data and have no cached data to show
    return taskFor(this.reload).isRunning && !this._hasCachedData;
  }
}
